import axios from "axios";
import { jwtDecode } from "jwt-decode";

let axiosInstance;
let onAuthErrorCallback = null;

export const setOnAuthErrorCallback = (callback) => {
  onAuthErrorCallback = callback;
};

const isTokenExpired = (token) => {
  if (!token) return true;

  try {
    const { exp } = jwtDecode(token);
    if (!exp) return true;
    return Date.now() > Number(exp * 1000);
  } catch (e) {
    return true;
  }
};

const refreshToken = async () => {
  const storedRefreshToken = window.localStorage?.getItem("refreshToken");

  if (!storedRefreshToken) {
    throw new Error("No refresh token available");
  }

  const authAxios = axios.create();
  const response = await authAxios.get("/public/api/v1/auth/refresh", {
    headers: { "Refresh-token": storedRefreshToken },
  });

  const { token: newAccessToken, refreshToken: newRefreshToken } = response.data;

  window.localStorage.setItem("token", newAccessToken);
  window.localStorage.setItem("refreshToken", newRefreshToken);

  return newAccessToken;
};

export const refreshAxios = (token) => {
  axiosInstance = axios.create({
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  // Request interceptor for token refresh
  axiosInstance.interceptors.request.use(
    async (config) => {
      const currentToken = window.localStorage?.getItem("token");
      const refreshTokenExists = window.localStorage?.getItem("refreshToken");

      // Only try to refresh if we have both tokens and the access token is expired
      if (currentToken && refreshTokenExists && isTokenExpired(currentToken)) {
        try {
          const newToken = await refreshToken();
          config.headers.Authorization = `Bearer ${newToken}`;
        } catch (error) {
          // Token refresh failed, clear session
          window.localStorage.removeItem("token");
          window.localStorage.removeItem("refreshToken");
          // Don't throw - let the request go through and get 401 from server
        }
      } else if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor for auth errors
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        // Clear session
        window.localStorage.removeItem("token");
        window.localStorage.removeItem("refreshToken");

        // Trigger logout callback
        if (onAuthErrorCallback) {
          onAuthErrorCallback(error);
        }
      }

      return Promise.reject(error);
    }
  );
};

export const getAxiosInstance = () => {
  return axiosInstance;
};
